import {
  IMonitoringPromptRunMentionVerdict,
  MONITORING_PROMPT_RUN_VERDICT_COUNT_KEY,
  MONITORING_PROMPT_RUN_VERDICT_MAX,
  MONITORING_PROMPT_RUN_VERDICT_MIN,
  MonitoringPromptRunComparisonKind,
  MonitoringPromptRunParsed,
  MonitoringPromptRunUnitType,
  MonitoringPromptRunVerdictGrade,
} from '@edgebox/sync-core-data-definitions';
import type { AnswerPassageElement } from './answerPassageElements';
import { toneForMetric } from './bandVisuals.utils';
import { foldHost } from './citationSources.utils';
import { CellPaintByLine, LinePaint, lineIndicesForUnits, referenceKey, resolveTableRow } from './GovernanceAiAnswerDetail.utils';
import { formatRecommendationEvidence } from './recommendationEvidence';
import { VERDICT_PALETTE } from './governanceThemeColors';
import type { SentimentTone } from '../GovernanceAnalyticsAiAnswers.utils';
import { verdictScoreOf } from '../toneMetric';

/**
 * The answer read one passage at a time: what each unit of the analysed answer says, and about whom.
 *
 * The page already reads the answer brand by brand — hover a brand and its passages light up. This
 * is the other direction: point at a passage and see which brands it is about, the verdict it gives
 * each of them, the attributes it discusses each of them on, and the context around it — the tags
 * it carries, what it compares, the alternatives it names that nobody tracks, the sources behind it.
 *
 * Everything comes from the analysed answer the page already loads: the units and their lines, the
 * labels, the per-brand records the verdict grades live on, and the attribute records. Nothing here
 * fetches, and nothing is inferred from the text beyond locating the recommendation's quote.
 */

/** The fields of the analysed answer a passage is read from. */
export type PassageDocument = Pick<
  MonitoringPromptRunParsed,
  'markdown' | 'units' | 'labels' | 'resolution' | 'aspects' | 'explicitRecommendation' | 'citations'
>;

/**
 * Units that are not passages a reader points at. A table is read row by row — its rows are the
 * passages — and a rule or an image states nothing about anyone.
 */
const NON_PASSAGE_UNIT_TYPES: ReadonlySet<MonitoringPromptRunUnitType> = new Set([
  MonitoringPromptRunUnitType.Table,
  MonitoringPromptRunUnitType.ThematicBreak,
  MonitoringPromptRunUnitType.Image,
]);

/** The grades of the verdict scale, most favourable first — the order a legend reads in. */
export const VERDICT_GRADES_BEST_FIRST: readonly MonitoringPromptRunVerdictGrade[] = [2, 1, 0, -1, -2];

/** A stored grade, or undefined for anything that is not one of the scale's five integers. */
export function asVerdictGrade(value: unknown): MonitoringPromptRunVerdictGrade | undefined {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MONITORING_PROMPT_RUN_VERDICT_MIN &&
    value <= MONITORING_PROMPT_RUN_VERDICT_MAX
    ? (value as MonitoringPromptRunVerdictGrade)
    : undefined;
}

/**
 * A grade placed on the verdict's 0–1 scale, where the verdict bands are drawn — so a passage's grade
 * and an answer's average verdict are read against the same five bands.
 */
export function verdictGradeScore(grade: MonitoringPromptRunVerdictGrade): number {
  return (grade - MONITORING_PROMPT_RUN_VERDICT_MIN) / (MONITORING_PROMPT_RUN_VERDICT_MAX - MONITORING_PROMPT_RUN_VERDICT_MIN);
}

/** What each grade is called when it describes one passage. */
export const PASSAGE_VERDICT_LABELS: Readonly<Record<MonitoringPromptRunVerdictGrade, string>> = {
  [2]: 'Recommends',
  [1]: 'Favourable',
  [0]: 'Descriptive or balanced',
  [-1]: 'Raises a caveat',
  [-2]: 'Argues against',
};

/**
 * What each grade means for one passage, in the rubric's own terms.
 *
 * Not the answer-level wording: those sentences describe an average over the answers, and a passage
 * carries one grade, so "on average" would be false here.
 */
export const PASSAGE_VERDICT_HINTS: Readonly<Record<MonitoringPromptRunVerdictGrade, string>> = {
  [2]: 'This passage recommends it without qualification, or names it the clearly better option.',
  [1]: 'This passage makes a favourable point about it, or endorses it with a condition.',
  [0]: 'This passage describes it, or weighs it evenly.',
  [-1]: 'This passage raises a cost, a limitation or a caveat about it.',
  [-2]: 'This passage argues against it, or names it the clearly worse option.',
};

/** One brand of one passage: how the passage speaks about it, and what it discusses it on. */
export interface PassageBrand {
  termKey: string;
  /** The verdict judge's grade for this brand in this passage; absent where the pair was never graded. */
  verdict?: MonitoringPromptRunVerdictGrade;
  /** The attributes this passage discusses this brand on, as tag keys, in the order first met. */
  attributeTermKeys: string[];
  /** The table column this brand was credited with, in a row that compares brands side by side. */
  column?: number;
  /** Whether the answer names this brand here for the first time. */
  firstMention: boolean;
}

/** A source behind a passage: the engine's own reference for it, or one an attribute record anchored. */
export interface PassageSource {
  url: string;
  /** The site the source is on, which its favicon is fetched for. */
  host: string;
  /** What the source reads as: its title — the site's brand where the title runs long — else the end of its address. */
  label: string;
  /** The page's full title, where the answer recorded one. */
  title?: string;
}

/** A comparison the passage draws. */
export interface PassageComparison {
  /** Stated outright rather than implied. */
  explicit: boolean;
  /** The tracked brand it compares against, when there is one. */
  counterpartTermKey?: string;
  /** Whether it compares against an alternative nobody tracks. */
  counterpartUntracked: boolean;
}

/** One passage of the answer, as a reader points at it. */
export interface AnswerPassage {
  unitId: string;
  type?: MonitoringPromptRunUnitType;
  /** The 0-based line of the answer's markdown the passage's rendered element starts on. */
  lineStart: number;
  lineEnd: number;
  /**
   * The brands the passage is about. Side by side in table order in a row that compares them;
   * otherwise the brand the passage is most about first.
   */
  brands: PassageBrand[];
  /** The tags the passage carries. */
  tagTermKeys: string[];
  /** Alternatives the passage names that are not tracked. */
  untrackedCoSubjects: string[];
  comparison?: PassageComparison;
  sources: PassageSource[];
  /** The brand the answer's recommendation names, when its quote is found in this passage and no other. */
  recommendsTermKey?: string;
}

/** Every passage of an answer, and how to find one from a rendered element. */
export interface AnswerPassageIndex {
  byId: ReadonlyMap<string, AnswerPassage>;
  /** Passage ids in reading order. */
  order: readonly string[];
  /** The passage whose rendered element starts on a line of the markdown. */
  idAtLine: ReadonlyMap<number, string>;
}

export const EMPTY_PASSAGE_INDEX: AnswerPassageIndex = { byId: new Map(), order: [], idAtLine: new Map() };

/**
 * The shortest recommendation quote located in the text. Anything shorter — a bare brand name, "the
 * best" — occurs in too many passages to point at one, and the badge is never guessed.
 */
const MIN_LOCATED_QUOTE_LENGTH = 12;

/** How many characters of a passage the card quotes back, so the reader sees which passage it is. */
export const PASSAGE_EXCERPT_MAX_LENGTH = 90;

/** Adds `value` to the list under `key`, once. */
function addOnce<K>(map: Map<K, string[]>, key: K, value: string): void {
  if (!value) {
    return;
  }
  const list = map.get(key) ?? [];
  if (!list.includes(value)) {
    list.push(value);
  }
  map.set(key, list);
}

/**
 * The longest title a source reads as in full. A longer one is a page's whole title — the headline and
 * the site's name, run together — so the source reads as the part that names the site's brand.
 */
export const SOURCE_TITLE_MAX_LENGTH = 40;

/** What separates a page's headline from the site's name in its title: a spaced bar, dash, bullet or arrow. */
const TITLE_SEPARATOR = /\s+[|–—·•»]\s+|\s+-\s+|\s+::\s+/;

/**
 * Second-level labels that sit between a brand and a country's top-level domain (`bbc.co.uk`), so the
 * brand is the label before them.
 */
const GENERIC_SECOND_LEVEL_LABELS: ReadonlySet<string> = new Set(['ac', 'co', 'com', 'edu', 'go', 'gov', 'ne', 'net', 'or', 'org']);

/**
 * The shortest brand key matched anywhere inside a title part. A shorter one — `g2`, `hp` — must be a
 * whole word of it, or it would match inside unrelated words.
 */
const MIN_EMBEDDED_BRAND_KEY_LENGTH = 4;

/** Letters and digits only, lower case. */
function compactText(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** The site a URL belongs to, folded the way every source on the page is, or empty when it does not parse. */
function hostOf(url: string): string {
  try {
    return foldHost(new URL(url).hostname);
  } catch {
    return '';
  }
}

/** The label of a host that names its brand: `g2` for `learn.g2.com`, `bbc` for `www.bbc.co.uk`. */
function brandKeyOf(host: string): string {
  const labels = foldHost(host).split('.').filter(Boolean);
  if (labels.length > 1) {
    labels.pop();
  }
  if (labels.length > 1 && GENERIC_SECOND_LEVEL_LABELS.has(labels[labels.length - 1])) {
    labels.pop();
  }
  return compactText(labels[labels.length - 1] ?? '');
}

/** Whether one part of a title names the brand behind `brandKey`. */
function namesBrand(part: string, brandKey: string): boolean {
  const compact = compactText(part);
  if (!brandKey || !compact) {
    return false;
  }
  const words = part.toLowerCase().split(/[^a-z0-9]+/);
  return (
    compact === brandKey || words.includes(brandKey) || (brandKey.length >= MIN_EMBEDDED_BRAND_KEY_LENGTH && compact.includes(brandKey))
  );
}

/**
 * What a titled source reads as: the title itself when it is short; when it runs long, the part of it
 * that names the site's brand ("G2 Learning Hub" out of "Acme vs Globex: Which Is Better? | G2
 * Learning Hub"), else its closing part, where a site conventionally puts its name. A long title with
 * no parts stays whole, and the chip ends it with an ellipsis.
 */
export function sourceTitleLabel(title: string, host: string): string {
  const whole = title.replace(/\s+/g, ' ').trim();
  if (whole.length <= SOURCE_TITLE_MAX_LENGTH) {
    return whole;
  }
  const parts = whole
    .split(TITLE_SEPARATOR)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 2) {
    return whole;
  }
  const brandKey = brandKeyOf(host);
  const naming = parts.filter((part) => namesBrand(part, brandKey));
  if (naming.length) {
    return naming.reduce((shortest, part) => (part.length < shortest.length ? part : shortest));
  }
  const closing = parts[parts.length - 1];
  return closing.length <= SOURCE_TITLE_MAX_LENGTH ? closing : whole;
}

/** The last part of a URL's path, decoded: `example-brand` for `…/p/12345/example-brand/`. */
export function lastPathSegment(url: string): string | undefined {
  let segment: string | undefined;
  try {
    segment = new URL(url).pathname.split('/').filter(Boolean).pop();
  } catch {
    return undefined;
  }
  if (!segment) {
    return undefined;
  }
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/**
 * What a source behind a passage reads as: the page's title where the answer recorded one, else the
 * engine's own label for it, else the last part of its address, and only for a site's front page,
 * which has no path, the site itself.
 */
export function passageSourceLabel(source: { url: string; title?: string; engineLabel?: string }): string {
  const host = hostOf(source.url);
  const title = source.title?.trim() || source.engineLabel?.trim();
  if (title) {
    return sourceTitleLabel(title, host);
  }
  return lastPathSegment(source.url) ?? (host || source.url);
}

/** A URL compared the way two records of one page line up: site case, `www.` and a closing slash aside. */
function sourceUrlKey(url: string): string {
  try {
    const parsed = new URL(url);
    return `${foldHost(parsed.hostname)}${parsed.pathname.replace(/\/+$/, '')}${parsed.search}`;
  } catch {
    return url.trim();
  }
}

/**
 * Markdown syntax stripped and typography folded, so the model's quote and the answer's own wording
 * compare word for word: links keep their text, emphasis and table pipes go, curly quotes become
 * straight ones, whitespace collapses and case is ignored.
 */
export function normalizeForMatching(text: string): string {
  return text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_`~|#>]/g, ' ')
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″«»]/g, '"')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** The recommendation's quote as it should be looked for: cleaned, and without an elided tail. */
function recommendationNeedle(evidence: string | undefined): string {
  const quote = normalizeForMatching(formatRecommendationEvidence(evidence));
  return quote
    .replace(/(\.\.\.|…)$/, '')
    .replace(/^["']+|["']+$/g, '')
    .trim();
}

/** The markdown source of a passage, lines joined. */
function passageSource(lines: readonly string[], passage: Pick<AnswerPassage, 'lineStart' | 'lineEnd'>): string {
  return lines.slice(passage.lineStart, passage.lineEnd + 1).join(' ');
}

/**
 * Reads every passage out of the analysed answer.
 *
 * A passage is a unit with lines of its own that is not debris, a table's frame, a rule or an image.
 * A table row is a passage whether it was persisted as a unit of its own or exists only as a
 * `<table>.rN` label, whose line is derived from the table's.
 */
export function buildAnswerPassageIndex(document: PassageDocument | undefined): AnswerPassageIndex {
  if (!document) {
    return EMPTY_PASSAGE_INDEX;
  }

  const units = document.units ?? [];
  const labels = document.labels ?? [];
  const resolution = document.resolution ?? [];
  const aspects = document.aspects ?? [];
  const unitById = new Map(units.map((unit) => [unit.id, unit]));

  const positions = new Map<string, { type?: MonitoringPromptRunUnitType; lineStart: number; lineEnd: number }>();
  for (const unit of units) {
    if (unit.debrisClass || typeof unit.lineStart !== 'number' || typeof unit.lineEnd !== 'number') {
      continue;
    }
    if (unit.type && NON_PASSAGE_UNIT_TYPES.has(unit.type)) {
      continue;
    }
    positions.set(unit.id, { type: unit.type, lineStart: unit.lineStart, lineEnd: unit.lineEnd });
  }
  for (const label of labels) {
    if (positions.has(label.unitId) || unitById.get(label.unitId)?.debrisClass) {
      continue;
    }
    const row = resolveTableRow(units, label.unitId);
    if (row) {
      positions.set(label.unitId, { type: MonitoringPromptRunUnitType.TableRow, lineStart: row.line, lineEnd: row.line });
    }
  }

  const brandOrder = new Map<string, string[]>();
  const columns = new Map<string, Map<string, number>>();
  const weights = new Map<string, Map<string, number>>();
  const verdicts = new Map<string, Map<string, MonitoringPromptRunVerdictGrade>>();
  const attributes = new Map<string, string[]>();
  const tags = new Map<string, string[]>();
  const untracked = new Map<string, string[]>();
  const comparisons = new Map<string, PassageComparison>();
  const sources = new Map<string, PassageSource[]>();

  // What the answer's records say about each page, by address: its title, from the citations, and
  // the site it is filed under, from the citations and then the attribute anchors. Gathered before any
  // passage reads them, so every chip of one page shows the same title and the same favicon.
  const titleByUrl = new Map<string, string>();
  const domainByUrl = new Map<string, string>();
  const recordPage = (url: string | undefined, facts: { title?: string; domain?: string }) => {
    const key = url ? sourceUrlKey(url) : '';
    const title = facts.title?.trim();
    const domain = foldHost(facts.domain);
    if (key && title && !titleByUrl.has(key)) {
      titleByUrl.set(key, title);
    }
    if (key && domain && !domainByUrl.has(key)) {
      domainByUrl.set(key, domain);
    }
  };
  for (const citation of document.citations ?? []) {
    recordPage(citation.url, citation);
  }
  for (const aspect of aspects) {
    for (const source of aspect.sources ?? []) {
      recordPage(source.url, { domain: source.domain });
    }
  }

  const addSource = (unitId: string, url: string | undefined, engineLabel?: string) => {
    const trimmed = String(url || '').trim();
    const key = sourceUrlKey(trimmed);
    const list = sources.get(unitId) ?? [];
    // One chip per page, however differently two records spell its address.
    if (!trimmed || list.some((source) => sourceUrlKey(source.url) === key)) {
      return;
    }
    const title = titleByUrl.get(key);
    list.push({
      url: trimmed,
      // The site the page is filed under, as on the page's other sources, so their favicons agree.
      host: domainByUrl.get(key) || hostOf(trimmed),
      label: passageSourceLabel({ url: trimmed, title, engineLabel }),
      ...(title ? { title } : {}),
    });
    sources.set(unitId, list);
  };

  for (const label of labels) {
    for (const subject of label.subjects ?? []) {
      addOnce(brandOrder, label.unitId, referenceKey(subject));
    }
    for (const cell of label.cellSubjects ?? []) {
      const termKey = referenceKey(cell.subject);
      if (!termKey || !Number.isInteger(cell.column) || cell.column < 0) {
        continue;
      }
      addOnce(brandOrder, label.unitId, termKey);
      const byBrand = columns.get(label.unitId) ?? new Map<string, number>();
      if (!byBrand.has(termKey)) {
        byBrand.set(termKey, cell.column);
      }
      columns.set(label.unitId, byBrand);
    }
    for (const topic of label.topics ?? []) {
      addOnce(tags, label.unitId, referenceKey(topic));
    }
    for (const name of label.untrackedCoSubjects ?? []) {
      addOnce(untracked, label.unitId, String(name || '').trim());
    }
    if (label.comparison && !comparisons.has(label.unitId)) {
      const counterpartTermKey = referenceKey(label.comparison.counterpartTerm) || undefined;
      comparisons.set(label.unitId, {
        explicit: label.comparison.kind === MonitoringPromptRunComparisonKind.Explicit,
        ...(counterpartTermKey ? { counterpartTermKey } : {}),
        counterpartUntracked: !!label.comparison.counterpartUntracked,
      });
    }
  }

  for (const record of resolution) {
    const termKey = referenceKey(record.entity);
    if (!termKey) {
      continue;
    }
    addOnce(brandOrder, record.unitId, termKey);
    if (typeof record.weight === 'number' && Number.isFinite(record.weight)) {
      const byBrand = weights.get(record.unitId) ?? new Map<string, number>();
      byBrand.set(termKey, Math.max(byBrand.get(termKey) ?? Number.NEGATIVE_INFINITY, record.weight));
      weights.set(record.unitId, byBrand);
    }
    const grade = asVerdictGrade(record.verdict);
    if (grade !== undefined) {
      const byBrand = verdicts.get(record.unitId) ?? new Map<string, MonitoringPromptRunVerdictGrade>();
      // Every record of one pair carries the same grade; the first one read answers for the pair.
      if (!byBrand.has(termKey)) {
        byBrand.set(termKey, grade);
      }
      verdicts.set(record.unitId, byBrand);
    }
  }

  // The engine's own references first: they carry its label for the page, which an attribute's
  // anchor to the same page does not.
  for (const unit of units) {
    for (const reference of unit.references ?? []) {
      addSource(unit.id, reference.url, reference.source);
    }
  }

  for (const aspect of aspects) {
    const termKey = referenceKey(aspect.entity);
    const attributeKey = referenceKey(aspect.aspectTerm);
    if (!termKey || !attributeKey) {
      continue;
    }
    addOnce(brandOrder, aspect.unitId, termKey);
    addOnce(attributes, `${aspect.unitId}::${termKey}`, attributeKey);
    for (const source of aspect.sources ?? []) {
      addSource(aspect.unitId, source.url);
    }
  }

  const order = Array.from(positions.entries())
    .sort(([, left], [, right]) => left.lineStart - right.lineStart || left.lineEnd - right.lineEnd)
    .map(([unitId]) => unitId);

  const seenBrands = new Set<string>();
  const byId = new Map<string, AnswerPassage>();
  const idAtLine = new Map<number, string>();

  for (const unitId of order) {
    const position = positions.get(unitId);
    if (!position) {
      continue;
    }
    const firstSeen = brandOrder.get(unitId) ?? [];
    const columnOf = columns.get(unitId);
    const weightOf = weights.get(unitId);
    const sortedKeys = firstSeen.slice().sort((left, right) => {
      if (columnOf?.size) {
        return (columnOf.get(left) ?? Number.MAX_SAFE_INTEGER) - (columnOf.get(right) ?? Number.MAX_SAFE_INTEGER);
      }
      const byWeight = (weightOf?.get(right) ?? Number.NEGATIVE_INFINITY) - (weightOf?.get(left) ?? Number.NEGATIVE_INFINITY);
      return byWeight || firstSeen.indexOf(left) - firstSeen.indexOf(right);
    });

    const brands = sortedKeys.map((termKey): PassageBrand => {
      const verdict = verdicts.get(unitId)?.get(termKey);
      const column = columnOf?.get(termKey);
      const firstMention = !seenBrands.has(termKey);
      seenBrands.add(termKey);
      return {
        termKey,
        ...(verdict !== undefined ? { verdict } : {}),
        attributeTermKeys: attributes.get(`${unitId}::${termKey}`) ?? [],
        ...(column !== undefined ? { column } : {}),
        firstMention,
      };
    });

    const comparison = comparisons.get(unitId);
    byId.set(unitId, {
      unitId,
      ...(position.type ? { type: position.type } : {}),
      lineStart: position.lineStart,
      lineEnd: position.lineEnd,
      brands,
      tagTermKeys: tags.get(unitId) ?? [],
      untrackedCoSubjects: untracked.get(unitId) ?? [],
      ...(comparison ? { comparison } : {}),
      sources: sources.get(unitId) ?? [],
    });
    if (!idAtLine.has(position.lineStart)) {
      idAtLine.set(position.lineStart, unitId);
    }
  }

  locateRecommendation(document, order, byId);

  return { byId, order, idAtLine };
}

/**
 * Marks the passage the answer's recommendation was quoted from — when, and only when, its quote is
 * found word for word in exactly one passage.
 *
 * The recommendation is stored as the recommended brand and a quote the analysis wrote down, with
 * nothing recording which passage the quote came from. A quote found in no passage (paraphrased,
 * spanning two) or in several gives no badge: pointing at the wrong passage would be worse than
 * pointing at none.
 */
function locateRecommendation(document: PassageDocument, order: readonly string[], byId: Map<string, AnswerPassage>): void {
  const recommendation = document.explicitRecommendation;
  const termKey = recommendation ? referenceKey(recommendation.entity) : '';
  if (!recommendation || !termKey) {
    return;
  }
  const needle = recommendationNeedle(recommendation.evidence);
  if (needle.length < MIN_LOCATED_QUOTE_LENGTH) {
    return;
  }

  const lines = (document.markdown || '').split('\n');
  const found = order.filter((unitId) => {
    const passage = byId.get(unitId);
    return !!passage && normalizeForMatching(passageSource(lines, passage)).includes(needle);
  });
  if (found.length !== 1) {
    return;
  }
  const passage = byId.get(found[0]);
  if (passage) {
    byId.set(found[0], { ...passage, recommendsTermKey: termKey });
  }
}

/**
 * The first words of a passage, markdown stripped, for the card to quote back — the cell that labels
 * the row for a table row.
 */
export function passageExcerpt(markdown: string, passage: Pick<AnswerPassage, 'lineStart' | 'lineEnd' | 'type'>): string {
  const lines = (markdown || '').split('\n');
  const source =
    passage.type === MonitoringPromptRunUnitType.TableRow
      ? ((lines[passage.lineStart] ?? '')
          .split('|')
          .map((cell) => cell.trim())
          .find((cell) => !!cell) ?? '')
      : passageSource(lines, passage);

  const text = source
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+[.)]\s+)/, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  return text.length > PASSAGE_EXCERPT_MAX_LENGTH ? `${text.slice(0, PASSAGE_EXCERPT_MAX_LENGTH - 1).trimEnd()}…` : text;
}

/** The band each grade of the scale reads as, so a grade is painted and drawn like the band it is. */
export const VERDICT_GRADE_TONES: Readonly<Record<MonitoringPromptRunVerdictGrade, SentimentTone>> = {
  [2]: 'excellent',
  [1]: 'good',
  [0]: 'neutral',
  [-1]: 'negative',
  [-2]: 'veryNegative',
};

function lensColors(grade: MonitoringPromptRunVerdictGrade): { solid: string; background: string } {
  const swatch = VERDICT_PALETTE[VERDICT_GRADE_TONES[grade]];
  return { solid: swatch.solid, background: swatch.tint };
}

/**
 * How each grade shades a passage in the verdict colouring: a light tint of the grade's colour, with
 * the full colour as the passage's accent bar. Read from the shared verdict palette, so a passage,
 * its key and the thumbs beside it agree.
 */
export const VERDICT_LENS_COLORS: Readonly<Record<MonitoringPromptRunVerdictGrade, { solid: string; background: string }>> = {
  [2]: lensColors(2),
  [1]: lensColors(1),
  [0]: lensColors(0),
  [-1]: lensColors(-1),
  [-2]: lensColors(-2),
};

/** The verdict colouring of the whole answer: the shade of every graded passage, and the tally. */
export interface VerdictLens {
  lines: (LinePaint | undefined)[];
  cells: CellPaintByLine;
  /** How many shaded passages — or, in a row comparing brands side by side, shaded cells — carry each grade. */
  counts: Readonly<Record<MonitoringPromptRunVerdictGrade, number>>;
  /** How many shaded passages grade their brands differently, and carry the underline that says so. */
  passagesGradedDifferently: number;
}

/**
 * Shades every passage by the verdict it gives, across every brand at once, so the brands can be
 * compared passage by passage — where each is praised, and where criticised.
 *
 * In a row that compares brands side by side, each brand's cell carries that brand's own grade. Any
 * other passage carries the grade of the brand it is most about; where it grades another brand
 * differently, a dashed underline says so, and the passage's card lists every brand's grade. A passage
 * that grades no brand stays unshaded — never neutral.
 */
export function buildVerdictLens(markdown: string, index: AnswerPassageIndex): VerdictLens {
  const lines: (LinePaint | undefined)[] = new Array((markdown || '').split('\n').length).fill(undefined);
  const cells: CellPaintByLine = new Map();
  const counts: Record<MonitoringPromptRunVerdictGrade, number> = { [2]: 0, [1]: 0, [0]: 0, [-1]: 0, [-2]: 0 };
  let passagesGradedDifferently = 0;

  for (const unitId of index.order) {
    const passage = index.byId.get(unitId);
    const graded = (passage?.brands ?? []).filter(
      (brand): brand is PassageBrand & { verdict: MonitoringPromptRunVerdictGrade } => brand.verdict !== undefined
    );
    if (!passage || !graded.length) {
      continue;
    }

    const celled = graded.filter((brand): brand is typeof brand & { column: number } => brand.column !== undefined);
    if (celled.length) {
      const row = cells.get(passage.lineStart) ?? new Map();
      for (const brand of celled) {
        // A column credited to two brands keeps the first, as the brand colouring does.
        if (row.has(brand.column)) {
          continue;
        }
        const colors = VERDICT_LENS_COLORS[brand.verdict];
        row.set(brand.column, { color: colors.solid, entityKey: brand.termKey, backgroundColor: colors.background, accent: true });
        counts[brand.verdict] += 1;
      }
      cells.set(passage.lineStart, row);
      continue;
    }

    const lead = graded[0];
    counts[lead.verdict] += 1;
    const colors = VERDICT_LENS_COLORS[lead.verdict];
    const gradedDifferently = graded.some((brand) => brand.verdict !== lead.verdict);
    if (gradedDifferently) {
      passagesGradedDifferently += 1;
    }
    for (let line = passage.lineStart; line <= passage.lineEnd && line < lines.length; line += 1) {
      lines[line] = {
        color: colors.solid,
        entityKey: lead.termKey,
        mixed: false,
        backgroundColor: colors.background,
        accent: true,
        ...(gradedDifferently ? { gradedDifferently } : {}),
      };
    }
  }

  return { lines, cells, counts, passagesGradedDifferently };
}

/** The verdict one brand gets where the answer discusses one attribute for it. */
export interface AttributeVerdict {
  /** How many of those passages carry each grade: the tally the verdict is combined from. */
  counts: IMonitoringPromptRunMentionVerdict;
  /** The verdict band the combined grades read in — the band a passage of that grade reads in. */
  tone: SentimentTone;
  /** The grade that band stands for, whose passage label names the verdict. */
  grade: MonitoringPromptRunVerdictGrade;
}

/** Every attribute's verdict for every brand it is discussed for: by attribute tag key, then by brand tag key. */
export type AttributeVerdicts = ReadonlyMap<string, ReadonlyMap<string, AttributeVerdict>>;

/** A tally with nothing counted in it. */
function emptyVerdictTally(): IMonitoringPromptRunMentionVerdict {
  return { arguesAgainst: 0, caveat: 0, neutral: 0, favourable: 0, recommends: 0 };
}

/**
 * The verdict each brand gets on each attribute the answer discusses it on, read from the passages
 * the answer draws.
 *
 * A brand's attribute is read from exactly the drawn passages a click on the attribute lights for
 * that brand: every passage element whose own lines (`passageLinesOf`, the lines the spotlight lights
 * it by) take in the lines of a unit the attribute's records file under the brand. A unit drawn as a
 * passage of its own lights that passage; a unit the answer draws as part of another passage — a
 * quoted line within a longer quote, an item with no card inside a list item — lights that passage; a
 * unit drawn outside every passage, with no card, lights no card and counts for nothing. Each passage
 * counts once, with the grade its card shows for the brand. The grades are combined the way a
 * brand's verdict over an answer's passages is: the mean grade, placed on the verdict's bands. So a
 * brand discussed at one grade throughout reads exactly that grade, in the same band and under the
 * same label as its passages.
 *
 * A passage whose card shows no grade for the brand adds nothing, as in the verdict colouring; a
 * brand and attribute whose passages carry no grade at all has no entry, and reads as not measured.
 */
export function buildAttributeVerdicts(
  document: PassageDocument | undefined,
  index: AnswerPassageIndex,
  elements: readonly AnswerPassageElement[]
): AttributeVerdicts {
  const units = document?.units ?? [];
  const tallies = new Map<string, Map<string, IMonitoringPromptRunMentionVerdict>>();
  const counted = new Set<string>();
  const litByUnit = new Map<string, Set<number>>();

  for (const aspect of document?.aspects ?? []) {
    const brandTermKey = referenceKey(aspect.entity);
    const attributeTermKey = referenceKey(aspect.aspectTerm);
    if (!brandTermKey || !attributeTermKey || !aspect.unitId) {
      continue;
    }
    // The lines a click on the attribute lights for this record, as the spotlight finds them.
    const lit = litByUnit.get(aspect.unitId) ?? lineIndicesForUnits(units, new Set([aspect.unitId]));
    litByUnit.set(aspect.unitId, lit);

    for (const element of elements) {
      if (!Array.from(element.lines).some((line) => lit.has(line))) {
        continue;
      }
      const grade = index.byId.get(element.passageId)?.brands.find((brand) => brand.termKey === brandTermKey)?.verdict;
      const counting = `${element.passageId}::${brandTermKey}::${attributeTermKey}`;
      if (grade === undefined || counted.has(counting)) {
        continue;
      }
      counted.add(counting);

      const byBrand = tallies.get(attributeTermKey) ?? new Map<string, IMonitoringPromptRunMentionVerdict>();
      const tally = byBrand.get(brandTermKey) ?? emptyVerdictTally();
      tally[MONITORING_PROMPT_RUN_VERDICT_COUNT_KEY[grade]] += 1;
      byBrand.set(brandTermKey, tally);
      tallies.set(attributeTermKey, byBrand);
    }
  }

  const verdicts = new Map<string, Map<string, AttributeVerdict>>();
  for (const [attributeTermKey, byBrand] of tallies) {
    const readings = new Map<string, AttributeVerdict>();
    for (const [brandTermKey, counts] of byBrand) {
      const tone = toneForMetric('verdict', verdictScoreOf(counts));
      // The verdict's bands are one per grade, so the band names the grade it stands for.
      const grade = VERDICT_GRADES_BEST_FIRST.find((candidate) => VERDICT_GRADE_TONES[candidate] === tone);
      if (tone && grade !== undefined) {
        readings.set(brandTermKey, { counts, tone, grade });
      }
    }
    verdicts.set(attributeTermKey, readings);
  }

  return verdicts;
}

/** One brand's verdict on one attribute, or undefined where no passage graded the brand on it. */
export function attributeVerdictFor(
  verdicts: AttributeVerdicts,
  { attributeTermKey, brandTermKey }: { attributeTermKey: string; brandTermKey: string }
): AttributeVerdict | undefined {
  return verdicts.get(attributeTermKey)?.get(brandTermKey);
}
